import React, { useState } from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Panel,
    Text,
    Textarea,
} from "@/ui";
import { Icon } from "@/components";
import {
    useForm,
    useFormActions,
    useMeta,
    useNotification,
    useValidator,
} from "@/hooks";
import {
    MUTATION_UPDATE_REDIRECT_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import isJSON from "validator/lib/isJSON";
import { JsonTreeEditor, PencilIcon } from "./shared";
import styles from "./GeneralsForm.module.scss";
import redirectStyles from "./RedirectForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;
const DiagramIcon = () => <i aria-hidden className="fas fa-project-diagram" />;

const RedirectForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, genericChange } = useForm();
    const [free, setFree] = useState(false);
    const forceUpdate = useState(0)[1];
    const notify = useNotification();

    const validate = () => {
        const json = document.getElementsByName("links")[0].value;

        if (isJSON(json)) return true;

        notify("Invalid Redirect structure.", "error");

        return false;
    };

    const switchMode = () => {
        const valid = free ? validate() : true;

        if (!valid) return;

        if (!free)
            document.getElementsByName("links")[0].value = JSON.stringify(
                inputs.links,
                null,
                1,
            );

        setFree(current => !current);
    };

    const changeOnFree = e => {
        const value = e.target.value;

        if (isJSON(value)) {
            genericChange(JSON.parse(value), e.target.name);
        } else {
            forceUpdate(update => update + 1);
        }
    };

    if (!_.get(inputs, "id", null)) {
        setInputs({
            ...app,
            links: JSON.parse(app.links || "[{}]"),
        });
    }

    const options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                links: JSON.stringify(inputs.links),
                type: "Redirect",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "links",
                method: "isJSON",
                validWhen: true,
                args: [],
                message: "Invalid Redirect structure.",
            },
        ],
        {
            ...inputs,
            links: free
                ? document.getElementsByName("links")[0]?.value
                : JSON.stringify(inputs.links),
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Redirect",
        "",
        MUTATION_UPDATE_REDIRECT_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    const linksError = validator.links?.isInvalid
        ? validator.links.message
        : undefined;

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Redirect</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Redirect Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "mode",
                                label: !free ? "Free mode" : "Visual mode",
                                icon: !free ? <PencilIcon /> : <DiagramIcon />,
                                variant: "secondary",
                                onClick: switchMode,
                            },
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Redirect actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    {!free ? (
                        <FormLayout.Section title="Redirect rule">
                            <FormLayout.Grid columns={1}>
                                <JsonTreeEditor
                                    onChange={json => {
                                        genericChange(json, "links");
                                    }}
                                    rootName="links"
                                    value={inputs.links ? inputs.links : {}}
                                />
                            </FormLayout.Grid>
                        </FormLayout.Section>
                    ) : null}
                    <div
                        className={
                            !free ? redirectStyles.hiddenField : undefined
                        }
                    >
                        <Textarea
                            error={linksError}
                            fullWidth
                            label="Redirect rule"
                            name="links"
                            onChange={changeOnFree}
                            rows={24}
                            textareaClassName={redirectStyles.freeTextarea}
                        />
                    </div>
                </FormLayout>
            )}
        </Panel>
    );
};

export default RedirectForm;
